/*
 * useEditDialog.tsx Ein einzelnes Datenfeld bearbeiten
 *
 * Created by Dr. Maximillian Dornseif 2023-11-10 in huWaWi3 33.7.0
 * Copyright (c) 2023 HUDORA GmbH
 */

import Form from '@hudora/hd-rjsf'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { assertIsObject, assertIsString } from 'assertate-debug'
import { DocumentNode } from 'graphql'
import { JSONSchema7 } from 'json-schema'
import * as React from 'react'

import { useHdDialog } from './HdDialog'
import { useToastMutation } from './useToastMutation'

export function useEditDialog({
  subSchema,
  propertyName,
  variables,
  mutation,
}: {
  subSchema: JSONSchema7
  propertyName: string
  variables: Record<string, any>
  mutation: DocumentNode
}): [(any) => void, React.ReactElement] {
  const submitFormRef = React.useRef(null)
  const [initialFormData, setInitialFormData] = React.useState()
  const [message, setMessage] = React.useState('')
  const [additionalVariables, setAdditionalVariables] = React.useState<Record<string, any>>({})
  const [results, mutateEntity] = useToastMutation({
    mutation,
    variables: { ...variables, fieldName: propertyName },
    fieldTitle: subSchema?.title,
  })
  const { fetching, error } = results
  const { openDialog, renderDialog, dismissDialog, isDialogOpen } = useHdDialog(
    `${subSchema?.title} bearbeiten`,
    'Daten im bestehenden Auftrag verändern.'
  )
  if (!subSchema) return [() => {}, <React.Fragment />]

  const edit = (initialData, variables = {}, message = '') => {
    setInitialFormData(initialData)
    setAdditionalVariables((prevState) => variables)
    setMessage((prevState) => message)
    openDialog()
  }

  const uebertragen = async (fieldData) => {
    const result2 = await mutateEntity({
      ...additionalVariables,
      value: fieldData,
      message: `${message.length > 0 ? message + ': ' : ''} ${propertyName} war ${JSON.stringify(
        initialFormData
      )}, jetzt ${JSON.stringify(fieldData)}`,
    } as any)
    // Das scheint nicht zu klappen.
    const { error } = result2
    if (error) alert(JSON.stringify(error))
    dismissDialog()
  }

  const dialog = (
    <>
      {renderDialog({
        renderBody: () => {
          assertIsString(propertyName)
          assertIsObject(subSchema, `kein Schema für ${propertyName}`)
          return error ? (
            <div>Ein Fehler ist aufgetreten: {JSON.stringify(error)}</div>
          ) : (
            <Form
              schema={subSchema}
              formData={initialFormData}
              liveValidate
              onSubmit={({ formData }) => uebertragen(formData)}
            >
              <button ref={submitFormRef} type="submit" style={{ display: 'none' }} />
            </Form>
          )
        },
        renderActions: () => (
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Button disabled={fetching} variant="outlined" onClick={dismissDialog}>
              Abbrechen
            </Button>
            <Button
              disabled={fetching}
              variant="contained"
              onClick={() => (submitFormRef?.current as any)?.click()}
            >
              Speichern
            </Button>
          </Stack>
        ),
      })}
    </>
  )

  return [edit, dialog]
}
